import { parse } from 'node-html-parser'
import path from 'path'

export type ImageInfo = {
	src: string
	isLocal: boolean
	syntax: 'html' | 'markdown'
	oldSrc: string
}

const generatePath = (src: string, folderPath: string) => {
	return path.join(folderPath, ...src.split('/'))
}

function escapeRegExp(string: string): string {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 *
 * This function will search for images in the content and return an array of image info
 * It will also update the content with the full path of the image on the local filesystem
 *
 * @param content @string - The content to search for images
 * @param folderPath @string - The path to the folder where the content is located
 * @returns
 */

export function findImagesInMdx({ content, folderPath }: { content: string; folderPath: string }): {
	images: ImageInfo[]
	updatedContent: string
} {
	const images: ImageInfo[] = []
	let updatedContent = content
	// Find HTML img tags
	const root = parse(content)
	const imgTags = root.querySelectorAll('img')
	imgTags.forEach((img) => {
		const src = img.getAttribute('src')
		if (src) {
			const isLocal = !src.startsWith('http')

			const localPath = generatePath(src, folderPath)

			images.push({
				src: isLocal ? localPath : src,
				isLocal,
				syntax: 'html',
				oldSrc: src,
			})
			// Update with the full path
			if (isLocal) {
				updatedContent = replaceImageSrc({
					content: updatedContent,
					oldSrc: src,
					newSrc: isLocal ? localPath : src,
				})
			}
		}
	})

	// Find Markdown image syntax
	const markdownImageRegex = /!\[.*?\]\((.*?)\)/g
	let match
	while ((match = markdownImageRegex.exec(content)) !== null) {
		const src = match[1]
		const isLocal = !src.startsWith('http')
		const localPath = generatePath(src, folderPath)

		images.push({
			src: isLocal ? localPath : src,
			isLocal,
			syntax: 'markdown',
			oldSrc: src,
		})
		// Update with the full path
		if (isLocal) {
			updatedContent = replaceMarkdownImageSrc({
				content: updatedContent,
				oldSrc: src,
				newSrc: isLocal ? localPath : src,
			})
		}
	}

	return { images, updatedContent }
}

function replaceImageSrc({
	content,
	oldSrc,
	newSrc,
}: {
	content: string
	oldSrc: string
	newSrc: string
}) {
	return content.replaceAll(`"${oldSrc}"`, `"${newSrc}"`)
}

function replaceMarkdownImageSrc({
	content,
	oldSrc,
	newSrc,
}: {
	content: string
	oldSrc: string
	newSrc: string
}): string {
	const markdownImageRegex = new RegExp(`!\\[([^\\]]*)\\]\\(${escapeRegExp(oldSrc)}\\)`, 'g')
	return content.replace(markdownImageRegex, (_, alt) => `![${alt}](${newSrc})`)
}

/**
 *
 * This function will replace the images in the content with the full path of the image on the CDN
 *
 * @param content @string - The content to search for images
 * @param localToUploadMap @object - The map of the local images to the uploaded images
 * @returns
 */
export function replacesHtmlAndMarkdownImagesWithOwnComponent(
	content: string,
	localToUploadMap: Record<string, string>,
): string {
	let updatedContent = content

	// Replace HTML img tags
	const root = parse(content)
	const imgTags = root.querySelectorAll('img')
	imgTags.forEach((img) => {
		const src = img.getAttribute('src')
		if (src) {
			const newSrc = localToUploadMap[src] || src
			const newImgTag = `<Image src="${newSrc}" alt="${img.getAttribute('alt') || ''}" />`
			// .toString() returns the img tag with the closing > which will not be matched by the regex
			updatedContent = updatedContent.replace(img.toString().replace('>', '/>'), newImgTag)
		}
	})

	// Replace Markdown image syntax
	const markdownImageRegex = /!\[(.*?)\]\((.*?)\)/g
	updatedContent = updatedContent.replace(markdownImageRegex, (_, alt, src) => {
		const newSrc = localToUploadMap[src] || src
		return `<Image src="${newSrc}" alt="${alt}" />`
	})

	return updatedContent
}
